import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { DocumentDiscussionAPI } from 'api/document/document-discussions';
import { CpBody02Primary } from 'components/atoms/customer-portal/typography';
import { Skeleton } from 'components/atoms/skeletions';
import { scrollToCommentInDoc } from 'components/organisms/document/doc-utils';
import { useDocuments } from 'components/organisms/document/document-store';
import { DocumentDiscussions } from 'components/organisms/document/parts/doc-discussions';
import { ContentCommentIdMap } from 'components/organisms/document/services/comment';
import { useGetDocumentForSpaceTab } from 'queries';
import { tryGetTextFromHTML } from 'utils/getTextFromHTML';
import { queueMacrotask } from 'utils/macrotask';

const Wrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
  background: var(--cp-white-100);
  padding-top: var(--cp-spacing-06);

  [id*='commentThread-rl-comment'] {
    border-radius: var(--border-radius-small);
  }

  #documentDiscussionThread {
    padding-top: var(--cp-spacing-05);
  }
`;

const Title = styled(CpBody02Primary)`
  margin-bottom: var(--cp-spacing-06);
  font-weight: bold;
  padding-left: var(--cp-spacing-06);
`;

const StyledSkeleton = styled(Skeleton)`
  height: 48px;
  padding: 0 var(--cp-spacing-06);
  .ant-skeleton-title {
    width: 100% !important;
    height: 35px;
    margin-bottom: var(--cp-spacing-05) 0;
  }
`;

const CommentSkeletons: React.FC = () => {
  return (
    <>
      <StyledSkeleton active />
      <StyledSkeleton active />
      <StyledSkeleton active />
    </>
  );
};

export const CpDocumentCommentsPane: React.FC<{
  projectId: number;
  spaceTabId: number;
}> = ({ spaceTabId, projectId }) => {
  const doc = useGetDocumentForSpaceTab(projectId, spaceTabId);
  const [, setDiscussionLength] = useState(0);
  const [commentThreadId] = useState('');
  const [editorCommentMap, setEditorCommentMap] = useState<ContentCommentIdMap>(
    {},
  );
  const [showDiscussionForm, setDiscussionFormVisibility] = useState(false);
  const [state] = useDocuments();
  const [newCommentBaseContent] = useState('');
  const onDiscussionFormClose = useCallback((val: boolean) => {
    setDiscussionFormVisibility(val);
  }, []);
  const loadedRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (doc && !loadedRef.current) {
      loadedRef.current = true;
      DocumentDiscussionAPI.getDiscussions(projectId, doc.documentId)
        .then((res) => {
          if (res) {
            setEditorCommentMap(
              res?.discussions
                // TODO: LOOKOUT FOR PERFORMANCE

                ?.reduce?.((acc, d) => {
                  if (d) {
                    const content = document.querySelector(
                      `[data-commentid="rl-comment-${d?.commentThread?.threadId}"]`,
                    )?.innerHTML;

                    if (content) {
                      const strippedContent = tryGetTextFromHTML(
                        content ?? d?.comments?.[0]?.content ?? '',
                      );

                      acc[d?.commentThread?.threadId] = {
                        content: strippedContent,
                        docId: doc?.documentId,
                        editorType: 'PUBLIC',
                        isActive: !d?.commentThread?.resolved,
                      };
                    }
                  }

                  return acc;
                }, {} as ContentCommentIdMap),
            );
          }
        })
        .finally(() => {
          queueMacrotask(() => {
            setIsLoading(false);
          });
        });
    }
  }, [doc, projectId]);

  return (
    <Wrapper>
      <Title>
        <FormattedMessage id="comments" />
      </Title>
      {doc && !isLoading ? (
        <DocumentDiscussions
          onActiveDiscussionLengthChange={setDiscussionLength}
          docId={doc?.documentId}
          projectId={projectId}
          onDiscussionClick={scrollToCommentInDoc}
          onCloseForm={() => {
            onDiscussionFormClose(false);
          }}
          editorCommentMap={editorCommentMap}
          showForm={showDiscussionForm}
          editor={state.editor}
          commentThreadId={commentThreadId}
          newCommentBaseContent={newCommentBaseContent}
        />
      ) : (
        <CommentSkeletons />
      )}
    </Wrapper>
  );
};
